import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const InvestmentCalculatorPage = () => {
  const [startAmount, setStartAmount] = useState('10000');
  const [additionalContribution, setAdditionalContribution] = useState('100');
  const [contributionFreq, setContributionFreq] = useState('monthly');
  const [years, setYears] = useState('10');
  const [returnRate, setReturnRate] = useState('7');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const P = parseFloat(startAmount) || 0;
    const PMT = parseFloat(additionalContribution) || 0;
    const t = parseFloat(years) || 1;
    const r = (parseFloat(returnRate) || 0) / 100;
    
    const periodsPerYear = contributionFreq === 'monthly' ? 12 : contributionFreq === 'quarterly' ? 4 : 1;
    const totalPeriods = t * periodsPerYear;
    const periodicRate = r / periodsPerYear;

    // Future value of initial investment
    const fvInitial = P * Math.pow(1 + r, t);
    
    // Future value of contributions
    const fvContributions = PMT * ((Math.pow(1 + periodicRate, totalPeriods) - 1) / periodicRate);
    
    const totalValue = fvInitial + fvContributions;
    const totalContributions = P + (PMT * totalPeriods);
    const totalInterest = totalValue - totalContributions;

    setResult({
      totalValue,
      totalContributions,
      totalInterest,
      initialGrowth: fvInitial - P,
      contributionGrowth: totalInterest - (fvInitial - P),
      principalPercent: ((totalContributions / totalValue) * 100).toFixed(0),
      interestPercent: ((totalInterest / totalValue) * 100).toFixed(0)
    });
  };

  const formatCurrency = (num) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(num);

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Investment Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Investment Calculator</h1>
          <p className="text-gray-400 mb-6">Calculate the future value of your investments with compound growth.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-40">Starting Amount</label>
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={startAmount} onChange={(e) => setStartAmount(e.target.value)}
                    className="w-32 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-40">Additional Contribution</label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">$</span>
                  <Input type="number" value={additionalContribution} onChange={(e) => setAdditionalContribution(e.target.value)}
                    className="w-28 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <select value={contributionFreq} onChange={(e) => setContributionFreq(e.target.value)}
                    className="px-3 py-2 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-white text-sm">
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-40">Length of Time</label>
                <div className="flex items-center gap-2">
                  <Input type="number" value={years} onChange={(e) => setYears(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <span className="text-gray-400">years</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <label className="text-gray-300 w-40">Estimated Return</label>
                <div className="flex items-center gap-1">
                  <Input type="number" step="0.1" value={returnRate} onChange={(e) => setReturnRate(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                  <span className="text-gray-400">%</span>
                </div>
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="text-center p-4 bg-[#1a1a1a] rounded-lg">
                      <p className="text-gray-400 mb-1">Future Value</p>
                      <p className="text-3xl font-bold text-green-400">{formatCurrency(result.totalValue)}</p>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Contributions</span>
                      <span className="text-white font-semibold">{formatCurrency(result.totalContributions)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-[#1a1a1a] rounded-lg">
                      <span className="text-gray-400">Total Interest Earned</span>
                      <span className="text-cyan-400 font-semibold">{formatCurrency(result.totalInterest)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col items-center justify-center">
                    <div className="relative w-32 h-32">
                      <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#06b6d4" strokeWidth="20"
                          strokeDasharray={`${result.principalPercent * 2.51} 251`} />
                        <circle cx="50" cy="50" r="40" fill="transparent" stroke="#f59e0b" strokeWidth="20"
                          strokeDasharray={`${result.interestPercent * 2.51} 251`}
                          strokeDashoffset={`-${result.principalPercent * 2.51}`} />
                      </svg>
                    </div>
                    <div className="mt-4 text-xs space-y-1">
                      <div className="flex items-center gap-2"><div className="w-3 h-3 bg-cyan-500 rounded"></div><span className="text-gray-400">{result.principalPercent}% Principal</span></div>
                      <div className="flex items-center gap-2"><div className="w-3 h-3 bg-amber-500 rounded"></div><span className="text-gray-400">{result.interestPercent}% Interest</span></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Financial Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'Compound Interest', path: '/compound-interest-calculator' }, { name: 'Retirement', path: '/retirement-calculator' }, { name: 'Interest', path: '/interest-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default InvestmentCalculatorPage;
